import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { COLOR, undoMoveDirection } from '../../../config/uiTokens'
import { ensureSlots, movedFromOf } from '../../../rules/gameLogic'
import ArmyBlock from '../ArmyBlock'
import DropZoneMarker from '../DropZoneMarker'
import {
    BOARD_W, BOARD_H, plotTileBox, SHADOW_DROP, SHADOW_RX, SHADOW_RY, PICKUP_SCALE,
    GROUND_SCALE, GROUND_SINK, GROUND_SHADOW_DROP, GROUND_SHADOW_RX, GROUND_SHADOW_RY, GROUND_SHADOW_OPACITY,
} from './geometry'

// ─── Hex board — drone tokens, deploy pluses, drop zones ──────────────────────
//
// The DOM layer above the SVG: the square board's ArmyBlock tiles, verbatim,
// each laid over its plot's hex block (one corridor pitch wide, three rows
// tall), positioned in percentages so it scales with the board. Each tile
// also gets a ground shadow drawn in the SVG beneath the airspace lanes.
//
// A FRESH DEPLOY (kind 'green' — a tapped-in drone, the opponent's replayed
// deploy, a drop zone's delivery) stands on the ground, not in the air: its
// tile is wrapped in .hexb__ground (GROUND_SCALE, sunk GROUND_SINK toward
// the land) and its shadow is the tight faint one a grounded thing casts.
// It lifts off when its player submits — the same uid comes back as a
// veteran and useTokenFlip grows it to flying size (DEPLOY_LIFT_MS) as the
// altitude shadow fades in.
//
// Per self corridor the layer also places, on the NEXT OPEN hosting slot:
//   • the white deploy plus while drones remain to deploy (canDeploy)
//   • the yellow drop-zone plus while drop zones are being placed
//     (placingDropZones), after any markers already pending
// and a parachute-crate marker on each slot a pending drop zone has claimed
// (tap to take it back) — dropZoneSlots names those slots, so the board can
// draw each zone's tether from its Base to the same slot. Same affordances
// as the square board, same size and black stroke; only the fill differs.
// A mouse resting on the placement plus reports it (onDropZoneHover) so the
// board can preview that zone's tether before the click.
//
// Each tile carries its identity for the slide effect (useTokenFlip.js):
// data-token-key (side + corridor + uid), data-token (its slot), data-kind
// (so a lift-off — green to vet under the same key — is seen), and
// data-entrance when the opponent's replay flagged it as a fresh deploy;
// the shadow carries the same key (data-shadow-key) so it glides along.
//
// Tiles are click-through: HexBoard hit-tests them geometrically for taps
// and pick-ups (a tapped drone opens its menu; any veteran or a moved-in
// drone lifts and follows the finger like a chess piece, snapping
// to the corridor under it on release). The pluses and markers are real
// buttons (data-plus) — HexBoard leaves gestures that start on them alone.
//
// buildTokens() returns { tokens (DOM), shadows (SVG) }.

const tileStyle = (box, lift = null) => ({
    position: 'absolute',
    left:   `${box.left   / BOARD_W * 100}%`,
    top:    `${box.top    / BOARD_H * 100}%`,
    width:  `${box.width  / BOARD_W * 100}%`,
    height: `${box.height / BOARD_H * 100}%`,
    transform: lift ? `translate(${lift.dx}px, ${lift.dy}px) scale(${PICKUP_SCALE})` : undefined,
    // opacity transitions for the intel peek's fade-back (HexBoard.css .hexb--peek)
    transition: lift ? 'none' : 'transform 160ms ease, opacity 140ms ease-out',
})

// A grounded tile's transform (.hexb__ground): sunk GROUND_SINK toward the
// land — as a share of the tile's own height, so it scales with the board —
// and shrunk to GROUND_SCALE about the tile's centre. useTokenFlip's
// lift-off animates a matured tile's block FROM this to none.
export const groundTransform = `translateY(${(GROUND_SINK / plotTileBox('self', 0, 0).height * 100).toFixed(3)}%) scale(${GROUND_SCALE})`

// The deploy plus — .bg__deploy-plus, BoardGrid's DeployPlus verbatim (also
// reused, yellow, as the drop-zone placement affordance).
function Plus({ onClick, color = '#fff', testId, onMouseEnter, onMouseLeave, pulse = false }) {
    return (
        <div className={`bg__deploy-plus${pulse ? ' hexb__plus-pulse' : ''}`} data-plus="true" data-testid={testId} onClick={onClick}
            onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <FontAwesomeIcon
                icon={faPlus}
                style={{ fontSize: 13, color, stroke: '#000', strokeWidth: 52, strokeLinejoin: 'round', paintOrder: 'stroke', pointerEvents: 'none' }}
            />
        </div>
    )
}

// The self corridor's slot affordances: the first open plot within the
// hosting capacity takes the deploy plus (behind every drone); pending drop
// zones claim the open plots after it, in placement order, and the
// placement plus sits on the next one after those. Returns { deploy: p |
// null, zones: [p, …] (one per pending zone here, in order), place: p |
// null }.
export function dropZoneSlots(bf0, cap0, { canDeploy = false, pending = 0, placing = false } = {}) {
    const bf = ensureSlots(bf0)
    const cap = Math.min(cap0 ?? bf.slots.length, bf.slots.length)
    const open = []
    for (let p = 0; p < cap; p++) if (bf.slots[p] === null) open.push(p)
    let next = 0
    const deploy = canDeploy && open.length > next ? open[next++] : null
    const zones = []
    for (let k = 0; k < pending && open.length > next; k++) zones.push(open[next++])
    const place = placing && open.length > next ? open[next++] : null
    return { deploy, zones, place }
}

// The placement cursor's drones: a funding tile shrunk to CURSOR_SCALE and
// centred on its end cell of the brood being placed (`at`, board units), so
// the pair flanks the construction between them. Left / top glide, so the
// pair flies down from its slots when placement opens and follows the
// cursor row to row.
export const CURSOR_SCALE = 0.5
export const CURSOR_GLIDE_MS = 140
const cursorTileStyle = (box, at) => ({
    ...tileStyle({ ...box, left: at.x - box.width / 2, top: at.y - box.height / 2 }),
    transform: `scale(${CURSOR_SCALE})`,
    transition: `left ${CURSOR_GLIDE_MS}ms ease-out, top ${CURSOR_GLIDE_MS}ms ease-out`,
    zIndex: 4,
})

export function buildTokens({
    selfBoard, oppBoard, hostingCaps,
    canDeploy = false, onDeploy = null,
    pulseDeploy = null,       // Set of corridors whose deploy plus pulses (the tutorial points at them)
    dropZones = [], placingDropZones = false, onDropZone = null, onDropZoneHover = null,
    moveDrag = null,          // { col, uid, dx, dy, mate } — the picked-up drone `uid` in `col`, following the finger; `mate` = the corridor-mate it hovers
    funding = null,           // { col, uids, at } — the two veterans paired to fund a Base while its spot is picked; at[uid] = the cursor end cell it hovers over (board units)
    launched = null,          // { col, self: Set(p), opp: Set(p) } — the clash corridor's slots whose last shot is away (tile fades)
    shake = null,             // { col, self: p|null, opp: p|null } — the clash corridor's slot recoiling right now
    hidden = null,            // Set('side-c-p') — slots whose resupply crate is still in flight (tile held back)
    landed = null,            // Set('side-c-p') — slots whose crate just touched down (tile fades in)
    modal = false,            // a placement mode owns the board: the pluses / markers go inert
}) {
    const tokens = [], shadows = []
    const pushSide = (side, board) => {
        if (!board?.length) return
        board.forEach((raw, c) => {
            const bf = ensureSlots(raw)
            bf.slots.forEach((token, p) => {
                if (!token) return
                const box = plotTileBox(side, c, p)
                // Resupply: a delivered drone's tile waits, unseen, for its crate
                // to land, then fades in (.bg__dz-landed).
                const slotKey = `${side}-${c}-${p}`
                const inFlight = !!hidden?.has(slotKey)
                const justLanded = !!landed?.has(slotKey)
                // Reveal: the tile recoils as its shot launches (.bfc--firing) and
                // fades once its last shot is away (.bfc--army-fading) — the same
                // classes the square board's cells wear, so BattleFx.css animates;
                // its ground shadow fades with it (.hexb__shadow--fading).
                const fading = launched?.col === c && !!launched[side]?.has(p)
                const firing = shake?.col === c && shake[side] === p
                const paired = side === 'self' && funding?.col === c && (funding.uids ?? []).includes(token.uid)
                const cursorAt = paired ? funding.at?.[token.uid] ?? null : null
                const shadowClass = [fading && 'hexb__shadow--fading', paired && 'hexb__shadow--paired'].filter(Boolean).join(' ') || undefined
                // On the ground: the tight faint shadow. In the air: the high one.
                const ground = token.kind === 'green'
                if (!inFlight) shadows.push(ground
                    ? <ellipse key={`${side}-${c}-${token.uid}`} data-shadow-key={`${side}-${c}-${token.uid}`} data-ground-shadow="true"
                        className={shadowClass}
                        cx={box.cx} cy={box.cy + GROUND_SINK + GROUND_SHADOW_DROP} rx={GROUND_SHADOW_RX} ry={GROUND_SHADOW_RY}
                        fill={`rgba(0, 0, 0, ${GROUND_SHADOW_OPACITY})`} filter="url(#hexb-shadow-blur)" />
                    : <ellipse key={`${side}-${c}-${token.uid}`} data-shadow-key={`${side}-${c}-${token.uid}`}
                        className={shadowClass}
                        cx={box.cx} cy={box.cy + SHADOW_DROP} rx={SHADOW_RX} ry={SHADOW_RY}
                        fill="rgba(0, 0, 0, 0.5)" filter="url(#hexb-shadow-blur)" />
                )
                const dragging = side === 'self' && moveDrag?.col === c && token.uid === moveDrag.uid
                const mate = side === 'self' && moveDrag?.col === c && moveDrag.mate != null && token.uid === moveDrag.mate
                const block = (
                    <ArmyBlock
                        side={side === 'self' ? 'player' : 'opp'}
                        kind={token.kind}
                        attacking={side === 'self' && !!bf.isAttacking}
                        delivered={!!token.delivered}
                        progress={token.progress || 0}
                        movedFromSide={token.kind === 'moved' ? undoMoveDirection(movedFromOf(bf, token)) : null}
                        gradientId={`hexb_${side}_${c}_${p}`}
                    />
                )
                tokens.push(
                    <div
                        key={`${side}-${c}-${token.uid}`}
                        className={`bg__token${fading ? ' bfc--army-fading' : ''}${firing ? ' bfc--firing' : ''}${paired ? ' hexb__token--paired' : ''}${mate ? ' hexb__token--mate' : ''}`}
                        data-token={slotKey}
                        data-token-key={`${side}-${c}-${token.uid}`}
                        data-kind={token.kind}
                        data-ground={ground ? 'true' : undefined}
                        data-delivered={token.delivered ? 'true' : undefined}
                        data-entrance={token.entrance || undefined}
                        data-paired={paired ? 'true' : undefined}
                        data-cursor={cursorAt ? 'true' : undefined}
                        data-mate={mate ? 'true' : undefined}
                        style={cursorAt ? cursorTileStyle(box, cursorAt) : { ...tileStyle(box, dragging ? moveDrag : null), zIndex: dragging ? 5 : undefined,
                                 visibility: inFlight ? 'hidden' : undefined }}
                    >
                        {ground
                            ? <div className="hexb__ground" style={{ transform: groundTransform }}>
                                {justLanded ? <div className="bg__dz-landed">{block}</div> : block}
                              </div>
                            : justLanded ? <div className="bg__dz-landed">{block}</div> : block}
                    </div>
                )
            })

            if (side !== 'self' || modal) return
            // Slot affordances (dropZoneSlots): the deploy plus, the pending
            // zones' markers, the placement plus — front to back.
            const pendingHere = dropZones.filter(z => z === c).length
            const slots = dropZoneSlots(bf, hostingCaps?.[c], { canDeploy, pending: pendingHere, placing: placingDropZones })
            if (slots.deploy != null) {
                const p = slots.deploy
                tokens.push(
                    <div key={`deploy-${c}`} className="bg__token" data-slot={`self-${c}-${p}`} style={tileStyle(plotTileBox('self', c, p))}>
                        <Plus testId={`hex-deploy-${c}`} pulse={!!pulseDeploy?.has(c)} onClick={(e) => { e.stopPropagation(); onDeploy?.(bf) }} />
                    </div>
                )
            }
            slots.zones.forEach((p, k) => {
                tokens.push(
                    <div key={`dz-${c}-${k}`} className="bg__token" data-slot={`self-${c}-${p}`} style={tileStyle(plotTileBox('self', c, p))}>
                        <div className="bg__dz-slot" data-plus="true" data-testid={`hex-dz-marker-${c}`}
                            onClick={(e) => { e.stopPropagation(); onDropZone?.(c, 'remove') }}>
                            <DropZoneMarker size={28} color={COLOR.DROP_ZONE} style={{ stroke: '#000000', strokeWidth: 40, paintOrder: 'stroke' }} />
                        </div>
                    </div>
                )
            })
            if (slots.place != null) {
                const p = slots.place
                tokens.push(
                    <div key={`dzplace-${c}`} className="bg__token" data-slot={`self-${c}-${p}`} style={tileStyle(plotTileBox('self', c, p))}>
                        <Plus color={COLOR.DROP_ZONE} testId={`hex-dz-place-${c}`} onClick={(e) => { e.stopPropagation(); onDropZone?.(c, 'place') }}
                            onMouseEnter={() => onDropZoneHover?.(c)} onMouseLeave={() => onDropZoneHover?.(null)} />
                    </div>
                )
            }
        })
    }
    pushSide('opp', oppBoard)
    pushSide('self', selfBoard)
    return { tokens, shadows }
}
